import {type BigId, type LobbyImage} from '@colonist/types'
import {OnlineStatus} from '../../../../../shared/friends/FriendTypes.ts'
import {createDivIn, createImgIn, createSpanIn, setElementInnerHTML, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {type TranslationObject} from '@colonist/i18n'
import {UIOnlineFriendController} from '../../ui_friends/UIOnlineFriendController.ts'
import {UIUrlController} from '../UIUrlController.ts'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables.ts'
import {AnalyticEventType, AnalyticEventCategory} from '../../../../../shared/entity_datas/EntityDatas.ts'
import {CA} from '../../analytics/Analytics.ts'
import {type AjaxDataTypes} from '../../../../../shared/AjaxDataTypes.ts'

interface StatusTextAndClassName {
    textStatus: string
    className: string
}

export class UIRoomFriendView {
    private readonly userId: BigId
    private statusText: HTMLElement
    private container: HTMLElement
    private inviteButton: HTMLElement
    private invitingDisabled = false
    private onlineStatus: OnlineStatus

    private readonly statusTextClassNames: string[] = ['room_friend_status_online', 'room_friend_status_in_room', 'room_friend_status_in_game', 'room_friend_status_offline']

    constructor(friendListContainer: HTMLElement, username: string, onlineStatus: OnlineStatus, icon: LobbyImage, userId: BigId) {
        this.createFriendContainer(friendListContainer, username, onlineStatus, icon)
        this.userId = userId
    }

    private createFriendContainer(friendListContainer: HTMLElement, username: string, onlineStatus: OnlineStatus, icon: LobbyImage) {
        const friendContainer = createDivIn(friendListContainer, 'room_friend_container')
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) friendContainer.classList.add('discord_mobile')
        this.container = friendContainer

        const friendInfo = createDivIn(friendContainer, 'room_friend_info')
        const inviteButton = createDivIn(friendContainer, 'btn_invite_friend btn_general')
        createSpanIn(inviteButton, 'btn_invite_friend_text', {key: 'strings:roomPage.invite'})
        const roomFriendUser = createDivIn(friendInfo, 'room_friend_user')
        this.inviteButton = inviteButton
        setElementOnClick(this.inviteButton, () => this.onInviteButtonClick())

        createImgIn(roomFriendUser, icon)
        const roomFriendUsernameAndStatus = createDivIn(roomFriendUser, 'room_friend_username_and_status')
        const friendUsernameTranslationObj: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}}
        createSpanIn(roomFriendUsernameAndStatus, 'room_friend_username', friendUsernameTranslationObj)

        const textAndColor = this.getTextAndClassNameForOnlineStatus(onlineStatus)
        const friendStatusTranslationObj: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: textAndColor.textStatus}}
        this.statusText = createSpanIn(roomFriendUsernameAndStatus, 'room_friend_status_text', friendStatusTranslationObj)
        this.statusText.classList.add(textAndColor.className)
        this.onlineStatus = onlineStatus
    }

    private getTextAndClassNameForOnlineStatus(onlineStatus: OnlineStatus): StatusTextAndClassName {
        switch(onlineStatus) {
            case OnlineStatus.OnlineAndIdle:
                return {textStatus: 'Online', className: this.statusTextClassNames[0]}
            case OnlineStatus.InRoom:
                return {textStatus: 'In room', className: this.statusTextClassNames[1]}
            case OnlineStatus.InGame:
                return {textStatus: 'In game', className: this.statusTextClassNames[2]}
            default:
                return {textStatus: 'Offline', className: this.statusTextClassNames[3]}
        }
    }

    private onInviteButtonClick() {
        if(this.invitingDisabled) return
        const roomId = UIUrlController.getRoomIdFromUrl()
        if(!roomId) return
        UIOnlineFriendController.inviteFriendToRoom(this.userId, roomId)

        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ClickedInviteFriendButton,
            category: AnalyticEventCategory.Room,
            stringValue: roomId,
        }
        void CA.sendAnalyticEventsWithMobileDimensions(eventData)
    }

    disableInviteButton() {
        this.inviteButton.classList.add('btn_invite_friend_clicked')
        setElementInnerHTML(this.inviteButton, {key: 'strings:colonistCommon.utils.emptyString'})
        createSpanIn(this.inviteButton, 'btn_invite_friend_text', {key: 'strings:roomPage.invited'})
        this.invitingDisabled = true
    }

    enableInviteButton() {
        if(!this.invitingDisabled) return
        this.inviteButton.classList.remove('btn_invite_friend_clicked')
        this.invitingDisabled = false
        setElementInnerHTML(this.inviteButton, {key: 'strings:colonistCommon.utils.emptyString'})
        createSpanIn(this.inviteButton, 'btn_invite_friend_text', {key: 'strings:roomPage.invite'})
    }

    getUserId(): string {
        return this.userId
    }

    updateOnlineStatus(onlineStatus: OnlineStatus) {
        const textAndColor = this.getTextAndClassNameForOnlineStatus(onlineStatus)
        this.statusText.innerText = textAndColor.textStatus
        this.statusText.classList.remove(...this.statusTextClassNames)
        this.statusText.classList.add(textAndColor.className)
        this.onlineStatus = onlineStatus
    }

    remove() {
        this.container.remove()
    }

    getOnlineStatus(): OnlineStatus {
        return this.onlineStatus
    }

    getContainer(): HTMLElement {
        return this.container
    }
}
